package ui.device.detail.editor

import androidx.compose.runtime.*
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.input.TextFieldValue
import api.AppType
import api.LanguageType
import api.TouchscreenRotation
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Fit_to_screen_outline
import compose.assets.svg.ScreenLand
import compose.assets.svg.ScreenPort
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.type.ResolutionStandard
import org.ssoft.common.type.ScreenRatio
import provider.CommonDisplayProvider
import provider.LocalAccountProvider
import provider.LocalDisplayProvider
import provider.LocalLocalization
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.*
import ui.widget.DropDownItem


interface DeviceEditorScope {

    val vm: DeviceEditorVM
    val action: (DeviceEditorVM.Action) -> Unit

    val args: DeviceEditorArgs
    val nav: Navigation
    val login: Io.User
    val display: CommonDisplayProvider
    val lang: LanguageType


    companion object {
        @Composable
        fun rememberScope(): DeviceEditorScope {

            val args = LocalNavigationArgs.current.rememberArgs(DeviceEditorArgs::class)
            val account = LocalAccountProvider.current
            val display = LocalDisplayProvider.current
            val nav = LocalNavigation.current

            val vm = nav.viewModel(DeviceEditorVM::class, args.deviceId.toString())
            val action = DeviceEditorVM.Action.rememberAction(vm)

            val locale = LocalLocalization.current
            return remember {
                object : DeviceEditorScope {
                    override val vm: DeviceEditorVM
                        get() = vm
                    override val action: (DeviceEditorVM.Action) -> Unit
                        get() = action
                    override val args: DeviceEditorArgs
                        get() = args
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val display: CommonDisplayProvider
                        get() = display
                    override val lang: LanguageType
                        get() = locale.language

                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(
        val registrationMode: Boolean,
        val data: Io.DeviceEditor,
        val canEditLayers: Boolean,
        val canEditDisplay: Boolean,
        val canEditLocation: Boolean,
        val canEditCompany: Boolean
    ) {


        lateinit var defaultAppLayerApps: List<DefaultLayerApp>
            private set

        private var volumeEnabledField = data.config.volume > 0F

        var change by mutableStateOf(false)
            private set

        suspend fun setup() {
            defaultAppLayerApps = DefaultLayerApp.build(data)
        }

        sealed class RotationDisplay(
            val shortcut: StringResource,
            val name: StringResource,
            val value: Int,
            val angle: Int,
            val icon: ImageVector,
            val isLand: Boolean
        ) {
            data object Land : RotationDisplay(
                Res.string.device_detail__display__rotation_horizontal,
                Res.string.device_detail__display__rotation_horizontal_full,
                0, 0, Svg.ScreenLand, true
            )

            data object Port : RotationDisplay(
                Res.string.device_detail__display__rotation_vertical,
                Res.string.device_detail__display__rotation_vertical_full,
                1, 0, Svg.ScreenPort, false
            )

            data object LandReversed :
                RotationDisplay(
                    Res.string.device_detail__display__rotation_horizontal_reversed,
                    Res.string.device_detail__display__rotation_horizontal_reversed_full,
                    2, 180, Svg.ScreenLand, true
                )

            data object PortReversed : RotationDisplay(
                Res.string.device_detail__display__rotation_vertical_reversed,
                Res.string.device_detail__display__rotation_vertical_reversed_full,
                3, 180, Svg.ScreenPort, false
            )

            companion object {
                val all by lazy {
                    arrayOf(Land, Port, LandReversed, PortReversed)
                }

                operator fun get(mask: Int): RotationDisplay {
                    return all.firstOrNull { it.value == mask } ?: Land
                }
            }
        }

        class RotationTouch(
            val type: TouchscreenRotation,
            val shortcut: StringResource,
            val name: StringResource,
            val angle: Int,
            val icon: ImageVector,
        ) {
            companion object {
                val all by lazy {
                    TouchscreenRotation.all.map {
                        when (it) {
                            TouchscreenRotation.DEFAULT -> RotationTouch(
                                type = it,
                                shortcut = Res.string.device_detail__display__rotation_default,
                                name = Res.string.device_detail__display__rotation_default,
                                angle = 0,
                                icon = Svg.Fit_to_screen_outline
                            )

                            TouchscreenRotation.HORIZONTAL -> RotationTouch(
                                type = it,
                                shortcut = Res.string.device_detail__display__rotation_horizontal,
                                name = Res.string.device_detail__display__rotation_horizontal_full,
                                angle = 0,
                                icon = Svg.ScreenLand
                            )

                            TouchscreenRotation.REVERSED_HORIZONTAL -> RotationTouch(
                                type = it,
                                shortcut = Res.string.device_detail__display__rotation_vertical,
                                name = Res.string.device_detail__display__rotation_vertical_full,
                                angle = 0,
                                icon = Svg.ScreenPort
                            )

                            TouchscreenRotation.REVERSED_VERTICAL -> RotationTouch(
                                type = it,
                                shortcut = Res.string.device_detail__display__rotation_horizontal_reversed,
                                name = Res.string.device_detail__display__rotation_horizontal_reversed_full,
                                angle = 180,
                                icon = Svg.ScreenLand
                            )

                            TouchscreenRotation.VERTICAL -> RotationTouch(
                                type = it,
                                shortcut = Res.string.device_detail__display__rotation_vertical_reversed,
                                name = Res.string.device_detail__display__rotation_vertical_reversed_full,
                                angle = 180,
                                icon = Svg.ScreenPort
                            )
                        }
                    }
                }
            }
        }


        class DefaultLayerApp(
            val id: Long,
            val label: String,
            val image: Long
        ) {
            companion object {
                suspend fun build(data: Io.DeviceEditor): List<DefaultLayerApp> = buildList {
                    add(
                        DefaultLayerApp(
                            id = -1,
                            label = Res.string.device_detail__default_app_default_kiosk.await(),
                            image = 127208300L
                        )
                    )
                    addAll(
                        data.apps
                            .filter { AppType.WWW_LINK match it.type }
                            .map {
                                DefaultLayerApp(
                                    id = it.id,
                                    label = it.name,
                                    image = it.logo
                                )
                            })
                }
            }
        }

        class ScreenRationProp(
            val type: ScreenRatio,
            override val label: String = type.label,
            override val image: ImageVector? = null
        ) : DropDownItem {
            companion object {
                val allHorizontal by lazy {
                    ScreenRatio.horizontal
                        .map { ScreenRationProp(it) }
                }
                val allVertical by lazy {
                    ScreenRatio.vertical
                        .map { ScreenRationProp(it) }
                }
            }
        }

        val screenResolution = mutableStateOf(ResolutionStandard.find(data.config.display))

        val screenRatio = EFieldRegularInt(
            property = data.config.display::screenRatio,
            onChange = { makeChange() })

        val rotationDisplay = EFieldRegularInt(
            property = data.config.display::rotation,
            onChange = { makeChange() })

        val rotationTouch = EFieldRegularInt(
            property = data.config.touch::rotation,
            onChange = { makeChange() })

        /**/

        val screenWidth = EFieldTextInt(
            property = data.config.display::width,
            condition = { it.trim().toIntOrNull()?.takeIf { it > 50 } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        val screenHeight = EFieldTextInt(
            property = data.config.display::height,
            condition = { it.trim().toIntOrNull()?.takeIf { it > 50 } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        /**/

        val name = EFieldTextString(
            property = data::name,
            condition = { it.trim().takeIf { it.length > 4 } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        val lat = EFieldTextDouble(
            property = data.address::lat,
            onChange = { makeChange() },
            onErr = { makeChange() })

        val hardwareId = EFieldRegularLong(
            property = data::hardwareId,
            onChange = { makeChange() })

        val lon = EFieldTextDouble(
            property = data.address::lon,
            onChange = { makeChange() },
            onErr = { makeChange() })

        val companyId = EFieldRegularLong(
            property = data::companyId,
            condition = { it.takeIf { it != -1L } },
            onChange = { makeChange() })

        val locationId = EFieldRegularLong(
            property = data::locationId,
            condition = { it.takeIf { it != -1L } },
            onChange = { makeChange() })

        val layerTop = EFieldRegularBoolean(
            property = data.config.layer::top,
            onChange = {
                configureLayers(true)
                makeChange()

            })
        val layerBottom = EFieldRegularBoolean(
            property = data.config.layer::bottom,
            onChange = {
                configureLayers(false)
                makeChange()
            })

        private fun configureLayers(invokeFromTop: Boolean) {
            if (data.config.layer.top == data.config.layer.bottom && !data.config.layer.top) {
                if (invokeFromTop) layerBottom.set(true)
                else layerTop.set(true)
            }
        }

        val showCursor = EFieldRegularBoolean(
            property = data.config::cursorVisible,
            onChange = { makeChange() })


        val volume = EFieldDualIntAsFloat(
            property = data.config::volume,
            onChange = { makeChange() })


        val photo = EFieldRegularLong(
            property = data::photoResId,
            onChange = { makeChange() })

        val videoScale = EFieldRegularInt(
            property = data.config.display::videoScale,
            onChange = { makeChange() })

        val defaultKioskAppId = EFieldRegularLong(
            property = data::defaultKioskAppId,
            onChange = { makeChange() })

        private fun makeChange() {
            change = allFields.any { it.change }
        }

        private val allFields by lazy {
            listOf(
                videoScale,
                screenRatio,
                rotationDisplay,
                rotationTouch,
                screenWidth,
                screenHeight,
                name,
                lat,
                lon,
                hardwareId,
                companyId,
                locationId,
                layerTop,
                layerBottom,
                showCursor,
                volume,
                photo,
                defaultKioskAppId
            )
        }

        init {
            if (registrationMode) {
                name.set(TextFieldValue(""))
            }
        }
    }
}

val ResolutionStandard.videoScaleName: StringResource
    get() {
        return if (ResolutionStandard.UNDEFINED == this) Res.string.device_detail__video_scale_auto
        else nameShort
    }

val ResolutionStandard.nameShort: StringResource
    get() = when (this) {
        ResolutionStandard.UNDEFINED -> Res.string.global__resolution__custom
        ResolutionStandard.FHD -> Res.string.global__resolution__fhd
        ResolutionStandard.HD -> Res.string.global__resolution__hd
        ResolutionStandard.HVGA -> Res.string.global__resolution__hvga
        ResolutionStandard.QHD -> Res.string.global__resolution__qhd
        ResolutionStandard.UHD -> Res.string.global__resolution__uhd
        ResolutionStandard.SVGA -> Res.string.global__resolution__svga
        ResolutionStandard.VGA -> Res.string.global__resolution__vga
    }

val ResolutionStandard.nameLong: StringResource
    get() = when (this) {
        ResolutionStandard.UNDEFINED -> Res.string.global__resolution__custom
        ResolutionStandard.FHD -> Res.string.global__resolution__fhd_full
        ResolutionStandard.HD -> Res.string.global__resolution__hd_full
        ResolutionStandard.HVGA -> Res.string.global__resolution__hvga_full
        ResolutionStandard.QHD -> Res.string.global__resolution__qhd_full
        ResolutionStandard.UHD -> Res.string.global__resolution__uhd_full
        ResolutionStandard.SVGA -> Res.string.global__resolution__svga_full
        ResolutionStandard.VGA -> Res.string.global__resolution__vga_full
    }

fun ResolutionStandard.Companion.find(display: Io.ImplDevice.Display): ResolutionStandard {
    return ResolutionStandard.all.firstOrNull {
        it.width == display.width && it.height == display.height ||
                it.width == display.height && it.width == display.height
    } ?: ResolutionStandard.UNDEFINED
}